import {
    Component,
    h,
    Host,
    Element,
    Event,
    Prop,
    State,
    Watch,
    forceUpdate,
    EventEmitter,
} from "@stencil/core";
import { i18nProvider } from "@trumpf/ux.i18n";

const mediaQueryString = "(min-width: 1260px)";

/**
 * @slot title
 * @slot copy - Slot for the long text on the detail layer (`<p>` or `<div>`)
 */
@Component({
    tag: "ux-iws-nb-usp-tile",
    styleUrl: "nb-usp-tile.scss",
    shadow: true,
})
export class NbUspTile {
    @Element() host: HTMLUxIwsNbUspTileElement;

    /** label for the open-button */
    @Prop() openButtonLabel: string = "More information";
    /** poster image for the video */
    @Prop() videoPoster: string = "";
    /** type of the video */
    @Prop() videoType: string = "video/mp4";
    /** video source */
    @Prop() video: string;
    /** link to the detail page */
    @Prop() link: string = "";
    /** label for the link */
    @Prop() linkLabel: string = "";
    /** use square layout */
    @Prop() square: boolean = false;

    private videoElement: HTMLVideoElement;
    private initialButton: HTMLButtonElement;
    private i18nSubscriberId: string;
    private mediaQuery: MediaQueryList = window.matchMedia(mediaQueryString);
    private prefersReducedMotion: boolean = window.matchMedia(
        "(prefers-reduced-motion: reduce)"
    ).matches;

    @State() isOpened: boolean = false;
    @State() isMobile: boolean = !window.matchMedia(mediaQueryString).matches;
    @State() isHovered: boolean = false;
    @State() isVideoPlaying: boolean = false;

    private mediaQueryListener = (e: MediaQueryListEvent) => {
        this.isMobile = !e.matches;
    };

    connectedCallback() {
        this.mediaQuery.addEventListener("change", this.mediaQueryListener);
        this.i18nSubscriberId = i18nProvider.subscribe(() => forceUpdate(this));
        this.handleVideoPlayback();
    }

    disconnectedCallback() {
        this.mediaQuery?.removeEventListener("change", this.mediaQueryListener);
        i18nProvider.unsubscribe(this.i18nSubscriberId);
    }

    @Watch("isOpened")
    @Watch("isMobile")
    @Watch("isHovered")
    handleVideoPlayback() {
        this.isVideoPlaying =
            (this.isHovered || this.isOpened || this.isMobile) &&
            !this.prefersReducedMotion;
    }

    @Watch("isVideoPlaying")
    handleVideoElement() {
        const { videoElement } = this;
        if (!videoElement) {
            return;
        }
        if (this.isVideoPlaying) {
            videoElement.play();
        } else {
            videoElement.pause();
        }
    }

    /** Event emitted when the layer is shown */
    @Event() uxShow: EventEmitter<{
        animationDuration: number;
    }>;

    /** Event emitted when the tile is focused */
    @Event() uxFocus: EventEmitter<void>;

    private closeLayer() {
        this.isOpened = false;
        this.initialButton.focus(); // remove focus from the hidden layer for a11y
    }

    render() {
        const { t } = i18nProvider;
        return (
            <Host
                class={{
                    "is-mobile": this.isMobile,
                    "is-opened": this.isOpened,
                    "is-hovered": this.isHovered,
                    "is-square": this.square,
                }}
            >
                <div class="initial-content">
                    <div class="media-container">
                        {this.video && (
                            <video
                                ref={(el) => (this.videoElement = el)}
                                poster={this.videoPoster}
                                loop
                                muted
                                playsinline
                                preload="auto"
                                autoplay={this.isVideoPlaying}
                            >
                                <source
                                    src={this.video}
                                    type={this.videoType}
                                />
                            </video>
                        )}
                        {!this.video && <img src={this.videoPoster} alt="" />}
                    </div>
                    <button
                        ref={(el) => {
                            this.initialButton = el;
                        }}
                        class="button-area"
                        aria-label={this.openButtonLabel}
                        onClick={() => {
                            if (this.isOpened) {
                                this.uxShow.emit({
                                    animationDuration: 0,
                                });
                                return;
                            }
                            this.isOpened = true;
                            this.uxShow.emit({
                                animationDuration: this.prefersReducedMotion
                                    ? 0
                                    : 300,
                            });
                        }}
                        onFocus={() => this.uxFocus.emit()}
                        onMouseEnter={() => (this.isHovered = true)}
                        onMouseLeave={() => (this.isHovered = false)}
                    >
                        <div class="button-area-content">
                            <div class="initial-title-container">
                                <slot name="initial-title" />
                            </div>
                            <div class="initial-button-container">
                                <div class="initial-button">
                                    <ux-icon name="plus" />
                                </div>
                            </div>
                        </div>
                    </button>
                </div>
                <div class="overflow-container">
                    <div
                        class="layer"
                        aria-hidden={!this.isOpened ? "true" : "false"}
                        inert={!this.isOpened}
                    >
                        <button
                            class="close-button"
                            onClick={() => this.closeLayer()}
                            onFocus={() => this.uxFocus.emit()}
                            aria-label={t("app.header.closeButton")}
                        >
                            <ux-icon name="cross" weight="normal" />
                        </button>
                        <div class="layer-content">
                            {this.isMobile && <slot name="layer-title" />}
                            <slot name="copy" />
                        </div>
                        <div class="link-container">
                            <a
                                class="button"
                                href={this.link}
                                onFocus={() => this.uxFocus.emit()}
                            >
                                {this.linkLabel} <ux-icon name="arrow-right" />
                            </a>
                        </div>
                    </div>
                </div>
            </Host>
        );
    }
}
